import {html, nothing, type TemplateResult} from 'lit';
import {live} from 'lit/directives/live.js';
import type {CategoryKey} from '../quiz-controller';

export function checkboxOption(
  id: string,
  label: string,
  checked: boolean,
  onChange: (checked: boolean) => void
): TemplateResult {
  return html`
    <!-- 行の見た目は 20px のまま、py-1.5 と -my-1.5 で当たり判定だけ 32px に広げる。
         負のマージンで相殺するので flex の行高＝初期描画の高さは変わらない。
         gap-3（12px）に対して上下 6px ずつなので隣の行と重ならない（py-3 だと重なって、
         母音の下端を押すと子音が入る） -->
    <label for=${id} class="flex items-center space-x-2 py-1.5 -my-1.5 cursor-pointer group">
      <span class="relative">
        <input
          type="checkbox"
          id=${id}
          .checked=${live(checked)}
          @change=${(e: Event) => onChange((e.target as HTMLInputElement).checked)}
          class="peer sr-only"
        />
        <span
          aria-hidden="true"
          class="block w-5 h-5 border rounded transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-teal-700 peer-focus-visible:ring-offset-2 ${checked
            ? 'bg-teal-600 border-teal-600'
            : 'border-gray-500 group-hover:border-teal-600'}"
        >
          ${checked
            ? html`<svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                class="w-4 h-4 text-white absolute top-0.5 left-0.5"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>`
            : nothing}
        </span>
      </span>
      <span class="text-sm text-gray-700">${label}</span>
    </label>
  `;
}

export interface QuizControlsProps {
  vowelsChecked: boolean;
  consonantsChecked: boolean;
  dependentVowelsChecked: boolean;
  viramaChecked: boolean;
  ayogavahaChecked: boolean;
  conjunctsChecked: boolean;
  numeralsChecked: boolean;
  characterCount: number;
  sliderMax: number;
  hasSelection: boolean;
  onToggle: (key: CategoryKey, value: boolean) => void;
  /** ドラッグ中。表示だけ更新する */
  onCharacterCountInput: (count: number) => void;
  /** 離した時点。出題プールを引き直す */
  onCharacterCountCommit: (count: number) => void;
}

const toCount = (e: Event): number => Number.parseInt((e.target as HTMLInputElement).value, 10);

export function quizControls(p: QuizControlsProps): TemplateResult {
  // スライダーの進捗塗り分け。旧実装は background-size が無く linear-gradient が
  // 全面を塗っていたので、トラックの色も進捗も一切見えていなかった。
  const filled =
    p.sliderMax > 2 ? ((p.characterCount - 2) / (p.sliderMax - 2)) * 100 : 0;

  return html`
    <section class="bg-teal-50 rounded-xl p-6 shadow-sm" aria-labelledby="dq-settings-heading">
      <h2 id="dq-settings-heading" class="text-lg sm:text-xl font-semibold text-teal-800 mb-4">
        クイズ設定 (Quiz Settings)
      </h2>

      <div class="space-y-5">
        <div role="group" aria-labelledby="dq-kinds-label">
          <span id="dq-kinds-label" class="sr-only">練習する文字の種類</span>
          <div class="flex flex-wrap gap-3 md:gap-5">
            ${checkboxOption('dq-vowels', '母音 (Vowels)', p.vowelsChecked, (v) =>
              p.onToggle('vowelsChecked', v)
            )}
            ${checkboxOption('dq-consonants', '子音 (Consonants)', p.consonantsChecked, (v) =>
              p.onToggle('consonantsChecked', v)
            )}
            ${checkboxOption(
              'dq-dependent-vowels',
              '依存母音記号 (Dependent Vowels)',
              p.dependentVowelsChecked,
              (v) => p.onToggle('dependentVowelsChecked', v)
            )}
            ${checkboxOption('dq-virama', 'ヴィラーマ (Virāma)', p.viramaChecked, (v) =>
              p.onToggle('viramaChecked', v)
            )}
            ${checkboxOption(
              'dq-ayogavaha',
              'アヌスヴァーラ・ヴィサルガ (Anusvāra / Visarga)',
              p.ayogavahaChecked,
              (v) => p.onToggle('ayogavahaChecked', v)
            )}
            ${checkboxOption('dq-conjuncts', '結合子音 (Conjuncts)', p.conjunctsChecked, (v) =>
              p.onToggle('conjunctsChecked', v)
            )}
            ${checkboxOption('dq-numerals', '数字 (Numerals)', p.numeralsChecked, (v) =>
              p.onToggle('numeralsChecked', v)
            )}
          </div>
        </div>

        <div class="pt-2">
          <label for="dq-count" class="block text-sm font-medium text-teal-800 mb-2">
            文字数 (Character Count): <span class="font-bold">${p.characterCount}</span>
          </label>
          <div class="flex items-center gap-3">
            <span aria-hidden="true" class="text-xs text-teal-700">2</span>
            <input
              type="range"
              id="dq-count"
              min="2"
              max=${p.sliderMax}
              step="1"
              .value=${live(String(p.characterCount))}
              ?disabled=${!p.hasSelection}
              aria-valuetext="${p.characterCount}文字"
              style="--dq-range-fill: ${filled}%"
              @input=${(e: Event) => p.onCharacterCountInput(toCount(e))}
              @change=${(e: Event) => p.onCharacterCountCommit(toCount(e))}
              class="w-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span aria-hidden="true" class="text-xs text-teal-700">${p.sliderMax}</span>
          </div>
          ${p.hasSelection
            ? nothing
            : html`<p class="mt-3 text-sm text-teal-900">
                練習する文字の種類を 1 つ以上選んでください。
              </p>`}
        </div>
      </div>
    </section>
  `;
}
